import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Link as LinkIcon, Eye, EyeSlash, Monitor, DeviceMobile } from '@phosphor-icons/react';
import { Footer } from './Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ['en', 'fr', 'mg'];

const emptyI18n = () => ({ en: '', fr: '', mg: '' });

const I18nFields = ({ value, onChange, label }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold">{label}</Label>
    {LANGS.map(l => (
      <Input
        key={l}
        placeholder={`${l.toUpperCase()}`}
        value={value[l] || ''}
        onChange={(e) => onChange({ ...value, [l]: e.target.value })}
        className="text-sm"
        data-testid={`i18n-input-${l}`}
      />
    ))}
  </div>
);

export const FooterManagement = () => {
  const [sections, setSections] = useState([]);
  const [pageChoices, setPageChoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' | 'mobile'
  const [previewNonce, setPreviewNonce] = useState(0);

  // Section dialog
  const [sectionDialog, setSectionDialog] = useState({ open: false, mode: 'create', data: null });
  // Link dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, mode: 'create', sectionId: null, data: null });
  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const [sec, pages] = await Promise.all([
        axios.get(`${API}/admin/footer`, { withCredentials: true }),
        axios.get(`${API}/admin/footer/pages-choices`, { withCredentials: true }),
      ]);
      setSections(sec.data || []);
      setPageChoices(pages.data || []);
      setPreviewNonce(n => n + 1);
    } catch (e) {
      toast.error('Failed to load footer data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  // ───── Section handlers ─────
  const openCreateSection = () => setSectionDialog({ open: true, mode: 'create', data: { key: '', title_i18n: emptyI18n() } });
  const openEditSection = (s) => setSectionDialog({ open: true, mode: 'edit', data: { section_id: s.section_id, key: s.key, title_i18n: { ...emptyI18n(), ...s.title_i18n } } });

  const saveSection = async () => {
    const { mode, data } = sectionDialog;
    if (!data.title_i18n.en && !data.title_i18n.fr && !data.title_i18n.mg) {
      toast.error('Title required in at least one language');
      return;
    }
    try {
      if (mode === 'create') {
        if (!data.key.trim()) { toast.error('Section key required'); return; }
        await axios.post(`${API}/admin/footer/sections`, { key: data.key.trim(), title_i18n: data.title_i18n }, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/footer/sections/${data.section_id}`, { title_i18n: data.title_i18n }, { withCredentials: true });
      }
      setSectionDialog({ open: false, mode: 'create', data: null });
      fetchSections();
      toast.success(mode === 'create' ? 'Section created' : 'Section updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/admin/footer/sections/${sectionId}`, { withCredentials: true });
      fetchSections();
      toast.success('Section deleted');
    } catch {
      toast.error('Failed');
    }
  };

  const moveSection = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.put(`${API}/admin/footer/reorder`, { section_ids: reordered.map(s => s.section_id) }, { withCredentials: true });
      fetchSections();
    } catch {
      toast.error('Reorder failed');
    }
  };

  // ───── Link handlers ─────
  const openCreateLink = (sectionId) => setLinkDialog({
    open: true, mode: 'create', sectionId,
    data: {
      title_i18n: emptyI18n(),
      url: '',
      external: false,
      is_active: true,
      page_slug: null,
      link_type: 'page', // UI-only: 'page' | 'external'
    }
  });

  const openEditLink = (sectionId, link) => setLinkDialog({
    open: true, mode: 'edit', sectionId,
    data: {
      ...link,
      title_i18n: { ...emptyI18n(), ...link.title_i18n },
      is_active: link.is_active !== false,
      link_type: (link.external || (link.url || '').startsWith('http')) ? 'external' : 'page',
    }
  });

  const saveLink = async () => {
    const { mode, sectionId, data } = linkDialog;
    const hasAnyTitle = LANGS.some(l => data.title_i18n[l]?.trim());
    if (!hasAnyTitle) { toast.error('Title required in at least one language'); return; }

    let url = (data.url || '').trim();
    let external = false;
    let page_slug = null;

    if (data.link_type === 'page') {
      if (!data.page_slug) { toast.error('Please pick a page from the dropdown'); return; }
      page_slug = data.page_slug;
      url = `/pages/${data.page_slug}`;
    } else {
      if (!url) { toast.error('URL required'); return; }
      if (!(url.startsWith('/') || url.startsWith('http'))) {
        toast.error("URL must start with '/' or 'http(s)://'"); return;
      }
      external = url.startsWith('http');
    }

    const payload = {
      title_i18n: data.title_i18n,
      url,
      external,
      is_active: !!data.is_active,
      page_slug,
    };
    try {
      if (mode === 'create') {
        await axios.post(`${API}/admin/footer/sections/${sectionId}/links`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/footer/sections/${sectionId}/links/${data.link_id}`, payload, { withCredentials: true });
      }
      setLinkDialog({ open: false, mode: 'create', sectionId: null, data: null });
      fetchSections();
      toast.success(mode === 'create' ? 'Link added' : 'Link updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const toggleLinkActive = async (sectionId, link) => {
    const nextActive = !(link.is_active !== false);
    try {
      await axios.put(
        `${API}/admin/footer/sections/${sectionId}/links/${link.link_id}`,
        { is_active: nextActive },
        { withCredentials: true }
      );
      fetchSections();
      toast.success(nextActive ? 'Link enabled' : 'Link disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteLink = async (sectionId, linkId) => {
    try {
      await axios.delete(`${API}/admin/footer/sections/${sectionId}/links/${linkId}`, { withCredentials: true });
      fetchSections();
      toast.success('Link deleted');
    } catch {
      toast.error('Failed');
    }
  };

  const moveLink = async (sectionId, links, idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= links.length) return;
    const reordered = [...links];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.put(`${API}/admin/footer/sections/${sectionId}/links/reorder`, { link_ids: reordered.map(l => l.link_id) }, { withCredentials: true });
      fetchSections();
    } catch {
      toast.error('Reorder failed');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="footer-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text_main">Footer Management</h2>
          <p className="text-sm text-text_muted mt-1">Manage footer sections and links. Changes reflect instantly on the site.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={previewOpen ? 'default' : 'outline'}
            onClick={() => { setPreviewOpen(v => !v); setPreviewNonce(n => n + 1); }}
            data-testid="toggle-preview-button"
          >
            {previewOpen ? <EyeSlash size={16} weight="regular" className="mr-1" /> : <Eye size={16} weight="regular" className="mr-1" />}
            {previewOpen ? 'Hide preview' : 'Show preview'}
          </Button>
          <Button onClick={openCreateSection} data-testid="add-section-button">
            <Plus size={16} weight="bold" className="mr-1" /> Add Section
          </Button>
        </div>
      </div>

      {previewOpen && (
        <div className="border border-border rounded-lg bg-surface_muted p-4" data-testid="footer-preview-wrapper">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-text_muted uppercase tracking-wider">Live Preview</p>
            <div className="flex items-center gap-1 bg-white border border-border rounded-full p-1">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${previewMode === 'desktop' ? 'bg-primary text-white' : 'text-text_muted hover:text-text_main'}`}
                data-testid="preview-desktop-button"
              >
                <Monitor size={14} weight="regular" /> Desktop
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${previewMode === 'mobile' ? 'bg-primary text-white' : 'text-text_muted hover:text-text_main'}`}
                data-testid="preview-mobile-button"
              >
                <DeviceMobile size={14} weight="regular" /> Mobile
              </button>
            </div>
          </div>
          <div className={`mx-auto bg-white border border-border rounded-md overflow-hidden transition-all ${previewMode === 'mobile' ? 'max-w-sm' : 'w-full'}`}>
            {/* nonce key forces full remount on every open → always fresh data */}
            <Footer key={previewNonce} />
          </div>
        </div>
      )}

      {sections.length === 0 && (
        <p className="text-sm text-text_muted text-center py-8">No sections yet.</p>
      )}

      <div className="space-y-4">
        {sections.map((section, sIdx) => (
          <div key={section.section_id} className="border border-border rounded-lg overflow-hidden bg-white" data-testid={`admin-section-${section.key}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-surface_muted border-b border-border">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} className="disabled:opacity-30" aria-label="Move up" data-testid={`section-up-${section.key}`}>
                    <ArrowUp size={14} weight="bold" />
                  </button>
                  <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1} className="disabled:opacity-30" aria-label="Move down" data-testid={`section-down-${section.key}`}>
                    <ArrowDown size={14} weight="bold" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text_main text-sm truncate">
                    {section.title_i18n?.en || section.title_i18n?.fr || section.key}
                  </p>
                  <p className="text-xs text-text_muted">
                    key: <code>{section.key}</code> · {section.links?.length || 0} link{section.links?.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => openCreateLink(section.section_id)} data-testid={`add-link-${section.key}`}>
                  <Plus size={14} weight="bold" className="mr-1" /> Link
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEditSection(section)} data-testid={`edit-section-${section.key}`}>
                  <PencilSimple size={14} weight="bold" />
                </Button>
                <Button size="sm" variant="outline" className="text-danger" onClick={() => setConfirmDelete({ type: 'section', sectionId: section.section_id, name: section.title_i18n?.en || section.key })} data-testid={`delete-section-${section.key}`}>
                  <Trash size={14} weight="bold" />
                </Button>
              </div>
            </div>

            {/* Links */}
            <div className="divide-y divide-border">
              {(section.links || []).length === 0 && (
                <p className="text-xs text-text_muted p-4 text-center">No links. Click &quot;+ Link&quot; to add one.</p>
              )}
              {(section.links || []).map((link, lIdx) => {
                const active = link.is_active !== false;
                return (
                <div key={link.link_id} className={`flex items-center gap-3 px-4 py-3 ${active ? '' : 'bg-surface_muted/60'}`} data-testid={`admin-link-${link.link_id}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveLink(section.section_id, section.links, lIdx, -1)} disabled={lIdx === 0} className="disabled:opacity-30" aria-label="Move up">
                      <ArrowUp size={12} weight="bold" />
                    </button>
                    <button onClick={() => moveLink(section.section_id, section.links, lIdx, 1)} disabled={lIdx === (section.links.length - 1)} className="disabled:opacity-30" aria-label="Move down">
                      <ArrowDown size={12} weight="bold" />
                    </button>
                  </div>
                  <LinkIcon size={14} weight="regular" className="text-text_muted flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${active ? 'text-text_main' : 'text-text_muted line-through'}`}>
                      {link.title_i18n?.en || link.title_i18n?.fr || '(untitled)'}
                    </p>
                    <p className="text-xs text-text_muted truncate">
                      <code>{link.url}</code>
                      {link.page_slug && <span className="ml-2 bg-primary-soft text-primary px-1.5 rounded">page: {link.page_slug}</span>}
                      {link.external && <span className="ml-2 bg-warning-soft text-warning px-1.5 rounded">external</span>}
                      {!active && <span className="ml-2 bg-surface_muted text-text_muted px-1.5 rounded border border-border">inactive</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={active}
                      onCheckedChange={() => toggleLinkActive(section.section_id, link)}
                      data-testid={`toggle-active-${link.link_id}`}
                      aria-label={active ? 'Disable link' : 'Enable link'}
                    />
                    <Button size="sm" variant="ghost" onClick={() => openEditLink(section.section_id, link)}>
                      <PencilSimple size={14} weight="regular" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirmDelete({ type: 'link', sectionId: section.section_id, linkId: link.link_id, name: link.title_i18n?.en || link.url })}>
                      <Trash size={14} weight="regular" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ──── Section Dialog ──── */}
      <Dialog open={sectionDialog.open} onOpenChange={(v) => !v && setSectionDialog({ open: false, mode: 'create', data: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sectionDialog.mode === 'create' ? 'New Section' : 'Edit Section'}</DialogTitle>
          </DialogHeader>
          {sectionDialog.data && (
            <div className="space-y-4">
              {sectionDialog.mode === 'create' && (
                <div>
                  <Label className="text-xs font-semibold">Section Key (stable identifier, no spaces)</Label>
                  <Input
                    placeholder="e.g. marketplace"
                    value={sectionDialog.data.key}
                    onChange={(e) => setSectionDialog({ ...sectionDialog, data: { ...sectionDialog.data, key: e.target.value.toLowerCase().replace(/\s+/g, '_') } })}
                    data-testid="section-key-input"
                  />
                </div>
              )}
              <I18nFields
                label="Title (each language)"
                value={sectionDialog.data.title_i18n}
                onChange={(v) => setSectionDialog({ ...sectionDialog, data: { ...sectionDialog.data, title_i18n: v } })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialog({ open: false, mode: 'create', data: null })}>Cancel</Button>
            <Button onClick={saveSection} data-testid="save-section-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Link Dialog ──── */}
      <Dialog open={linkDialog.open} onOpenChange={(v) => !v && setLinkDialog({ open: false, mode: 'create', sectionId: null, data: null })}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{linkDialog.mode === 'create' ? 'New Link' : 'Edit Link'}</DialogTitle>
          </DialogHeader>
          {linkDialog.data && (
            <div className="space-y-4">
              <I18nFields
                label="Title (each language)"
                value={linkDialog.data.title_i18n}
                onChange={(v) => setLinkDialog({ ...linkDialog, data: { ...linkDialog.data, title_i18n: v } })}
              />

              <div>
                <Label className="text-xs font-semibold">Link destination</Label>
                <Select
                  value={linkDialog.data.link_type}
                  onValueChange={(v) => setLinkDialog({
                    ...linkDialog,
                    data: {
                      ...linkDialog.data,
                      link_type: v,
                      // Reset the other side to avoid stale values.
                      page_slug: v === 'page' ? linkDialog.data.page_slug : null,
                      url: v === 'external' ? linkDialog.data.url : '',
                    }
                  })}
                >
                  <SelectTrigger data-testid="link-behavior-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="page">Point to a Page (recommended)</SelectItem>
                    <SelectItem value="external">External URL</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-text_muted mt-1">
                  Pages are managed in the <strong>Pages</strong> section. The Footer only stores the reference.
                </p>
              </div>

              {linkDialog.data.link_type === 'page' ? (
                <div>
                  <Label className="text-xs font-semibold">Page</Label>
                  <Select
                    value={linkDialog.data.page_slug || ''}
                    onValueChange={(v) => setLinkDialog({ ...linkDialog, data: { ...linkDialog.data, page_slug: v } })}
                  >
                    <SelectTrigger data-testid="link-page-select">
                      <SelectValue placeholder="Select a page…" />
                    </SelectTrigger>
                    <SelectContent>
                      {pageChoices.length === 0 && (
                        <SelectItem value="__none" disabled>No published pages available</SelectItem>
                      )}
                      {pageChoices.map(p => (
                        <SelectItem key={p.slug} value={p.slug} data-testid={`page-choice-${p.slug}`}>
                          {(p.title?.en || p.title?.fr || p.slug)} — <code className="text-[10px]">/pages/{p.slug}</code>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-semibold">External URL</Label>
                  <Input
                    placeholder="https://example.com or /custom-route"
                    value={linkDialog.data.url}
                    onChange={(e) => setLinkDialog({ ...linkDialog, data: { ...linkDialog.data, url: e.target.value } })}
                    data-testid="link-url-input"
                  />
                  <p className="text-[11px] text-text_muted mt-1">Must start with <code>/</code> (internal route) or <code>http(s)://</code></p>
                </div>
              )}

              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                <div>
                  <Label className="text-xs font-semibold">Active</Label>
                  <p className="text-[11px] text-text_muted">Turn off to hide this link from the public footer (page remains editable in Pages).</p>
                </div>
                <Switch
                  checked={linkDialog.data.is_active !== false}
                  onCheckedChange={(v) => setLinkDialog({ ...linkDialog, data: { ...linkDialog.data, is_active: v } })}
                  data-testid="link-active-switch"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, mode: 'create', sectionId: null, data: null })}>Cancel</Button>
            <Button onClick={saveLink} data-testid="save-link-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Delete Confirm ──── */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{confirmDelete?.name}</strong>.
              {confirmDelete?.type === 'section' && ' All links inside it will be deleted too.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger hover:bg-danger"
              onClick={async () => {
                if (confirmDelete.type === 'section') await deleteSection(confirmDelete.sectionId);
                else await deleteLink(confirmDelete.sectionId, confirmDelete.linkId);
                setConfirmDelete(null);
              }}
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
